import React, { createContext, useReducer, useCallback } from "react";

import DashboardReducer from "../reducers/dashboardReducer";

import axios from "../../axios";

import * as actionTypes from "../actionTypes";

import * as urlRequest from "../urls";

const initialState = {
  loading: false,

  bookingCounts: [],

  todayBookings: [],

  tomorrowBookings: [],

  // Interpreter Dashboard
  interpreterDashboard: null,
  interpreterDashboardLoading: false,

  // Activity Dashboard
  activityDashboard: null,
  activityDashboardLoading: false,

  // Financial Dashboard
  financialDashboard: null,
  financialDashboardLoading: false,

  error: null,
};

export const DashboardContext = createContext(initialState);

export const DashboardProvider = ({ children }) => {
  const [state, dispatch] = useReducer(DashboardReducer, initialState);

  // ==========================================
  // Booking Dashboard Counts
  // ==========================================

  const getDashboardsHandler = async () => {
    dispatch({ type: actionTypes.PROCESS_START });

    try {
      const result = await axios.get(urlRequest.Bookings + "/countbookings");

      dispatch({
        type: actionTypes.FETCH_RECORDS,
        payload: result.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_ERRORS,
        payload: error,
      });
    }
  };

  // ==========================================
  // Today's Bookings
  // ==========================================

  const getTodayBookingsHandler = async () => {
    dispatch({ type: actionTypes.PROCESS_START });

    try {
      const result = await axios.get(urlRequest.Bookings + "/todays");

      dispatch({
        type: "FETCH_TODAY_BOOKINGS",
        payload: result.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_ERRORS,
        payload: error,
      });
    }
  };

  // ==========================================
  // Tomorrow's Bookings
  // ==========================================

  const getTomorrowBookingsHandler = async (date) => {
    dispatch({ type: actionTypes.PROCESS_START });

    try {
      const result = await axios.get(urlRequest.Bookings + `/todays/${date}`);

      dispatch({
        type: "FETCH_TOMORROW_BOOKINGS",
        payload: result.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_ERRORS,
        payload: error,
      });
    }
  };

  // ==========================================
  // Interpreter Dashboard
  // ==========================================

  const getInterpreterDashboardHandler = async () => {
    dispatch({
      type: "INTERPRETER_DASHBOARD_START",
    });

    try {
      const result = await axios.get(urlRequest.Interpreters + "/dashboard");

      dispatch({
        type: "FETCH_INTERPRETER_DASHBOARD",
        payload: result.data,
      });
    } catch (error) {
      dispatch({
        type: "INTERPRETER_DASHBOARD_ERROR",
        payload: error,
      });
    }
  };

  // ==========================================
  // Financial Dashboard
  // ==========================================

  const getFinancialDashboardHandler = async () => {
    dispatch({
      type: "FINANCIAL_DASHBOARD_START",
    });

    try {
      const result = await axios.get(
        urlRequest.Bookings + "/financial-dashboard",
      );

      dispatch({
        type: "FETCH_FINANCIAL_DASHBOARD",
        payload: result.data,
      });
    } catch (error) {
      dispatch({
        type: "FINANCIAL_DASHBOARD_ERROR",
        payload: error,
      });
    }
  };

  // ==========================================
  // Activity Dashboard
  // ==========================================

  const getActivityDashboardHandler = useCallback(
    async (page = 1, limit = 10) => {
      dispatch({
        type: "ACTIVITY_DASHBOARD_START",
      });

      try {
        const result = await axios.get(urlRequest.ActivityLogs, {
          params: {
            page,
            limit,
          },
        });

        dispatch({
          type: "FETCH_ACTIVITY_DASHBOARD",
          payload: result.data,
        });
      } catch (error) {
        dispatch({
          type: "ACTIVITY_DASHBOARD_ERROR",
          payload: error,
        });
      }
    },
    [],
  );

  return (
    <DashboardContext.Provider
      value={{
        // Booking Dashboard
        loading: state.loading,
        data: state.bookingCounts,

        // Bookings
        todayBookings: state.todayBookings,
        tomorrowBookings: state.tomorrowBookings,

        // Interpreter Dashboard
        interpreterDashboard: state.interpreterDashboard,
        interpreterDashboardLoading: state.interpreterDashboardLoading,

        // Financial Dashboard
        financialDashboard: state.financialDashboard,
        financialDashboardLoading: state.financialDashboardLoading,

        // Activity Dashboard
        activityDashboard: state.activityDashboard,
        activityDashboardLoading: state.activityDashboardLoading,

        // Error
        error: state.error,

        // Handlers
        getDashboardsHandler,
        getTodayBookingsHandler,
        getTomorrowBookingsHandler,
        getInterpreterDashboardHandler,
        getFinancialDashboardHandler,
        getActivityDashboardHandler,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
